import { useClerk } from "@clerk/nextjs";
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AppConfig } from "@/config/app.config";
import { AuthContext } from "@/data/auth/auth.context";
import { logger } from "@/util/logger";

type SsePayload = {
  id?: string;
  event: string;
  data: unknown;
};

type SseContextType = {
  connected: boolean;
  lastEvent: SsePayload | null;
  addEventListener: (event: string, handler: (payload: SsePayload) => void) => () => void;
};

const SseContext = createContext<SseContextType>({} as never);

const parseData = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

export const SseProvider = ({ children }: PropsWithChildren) => {
  const { isLoggedIn, isInitializing } = AuthContext.useAuth();
  const { session } = useClerk();

  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<SsePayload | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isConnectingRef = useRef(false);
  const connectRef = useRef<() => Promise<void>>(async () => {});

  const listenersRef = useRef<Map<string, Set<(payload: SsePayload) => void>>>(new Map());
  const eventSourceHandlersRef = useRef<Map<string, (event: MessageEvent<string>) => void>>(new Map());

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const closeEventSource = useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    setConnected(false);
  }, []);

  const scheduleReconnect = useCallback((delayMs = 3000) => {
    if (reconnectTimerRef.current) {
      return;
    }
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      void connectRef.current();
    }, delayMs);
  }, []);

  const attachHandlersToSource = useCallback((source: EventSource) => {
    eventSourceHandlersRef.current.forEach((handler, event) => {
      source.addEventListener(event, handler as EventListener);
    });
  }, []);

  const ensureEventHandler = useCallback((event: string) => {
    if (eventSourceHandlersRef.current.has(event)) {
      return;
    }

    const handler = (message: MessageEvent<string>) => {
      const payload: SsePayload = {
        id: message.lastEventId || undefined,
        event,
        data: parseData(message.data),
      };

      setLastEvent(payload);

      const listeners = listenersRef.current.get(event);
      listeners?.forEach((listener) => listener(payload));
    };

    eventSourceHandlersRef.current.set(event, handler);

    if (eventSourceRef.current) {
      eventSourceRef.current.addEventListener(event, handler as EventListener);
    }
  }, []);

  const connect = useCallback(async () => {
    if (isConnectingRef.current || !session) {
      return;
    }

    isConnectingRef.current = true;

    const token = await session.getToken();
    if (!token) {
      logger.warn("[SSE] No token available, cannot connect");
      isConnectingRef.current = false;
      return;
    }

    clearReconnectTimer();
    closeEventSource();

    const url = `${AppConfig.api.url}/sse/events?token=${encodeURIComponent(token)}`;
    const source = new EventSource(url);

    eventSourceRef.current = source;
    attachHandlersToSource(source);

    source.onopen = () => {
      setConnected(true);
      isConnectingRef.current = false;
      logger.info("[SSE] Connected");
    };

    source.onerror = () => {
      setConnected(false);
      isConnectingRef.current = false;
      logger.warn("[SSE] Connection error, reconnecting");
      source.close();
      eventSourceRef.current = null;
      scheduleReconnect();
    };
  }, [session, clearReconnectTimer, closeEventSource, attachHandlersToSource, scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    if (isInitializing || !isLoggedIn || !session) {
      clearReconnectTimer();
      closeEventSource();
      isConnectingRef.current = false;
      return;
    }

    void connect();

    return () => {
      clearReconnectTimer();
      closeEventSource();
      isConnectingRef.current = false;
    };
  }, [isInitializing, isLoggedIn, session, connect, clearReconnectTimer, closeEventSource]);

  const addEventListener = useCallback(
    (event: string, handler: (payload: SsePayload) => void) => {
      ensureEventHandler(event);

      const listeners = listenersRef.current.get(event) ?? new Set();
      listeners.add(handler);
      listenersRef.current.set(event, listeners);

      return () => {
        const existing = listenersRef.current.get(event);
        if (!existing) {
          return;
        }
        existing.delete(handler);

        if (existing.size === 0) {
          listenersRef.current.delete(event);

          const eventHandler = eventSourceHandlersRef.current.get(event);
          if (eventHandler && eventSourceRef.current) {
            eventSourceRef.current.removeEventListener(event, eventHandler as EventListener);
          }
          eventSourceHandlersRef.current.delete(event);
        }
      };
    },
    [ensureEventHandler],
  );

  const value = useMemo(
    () => ({
      connected,
      lastEvent,
      addEventListener,
    }),
    [connected, lastEvent, addEventListener],
  );

  return <SseContext.Provider value={value}>{children}</SseContext.Provider>;
};

export const useSse = () => {
  return useContext(SseContext);
};
